import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import { Button } from "@/components/ui/button.jsx";
import { ShoppingCart, DollarSign, Calculator, Printer } from 'lucide-react';

export default function MaterialsList({ sections, jumps = [], parameters = {}, onCostCalculated }) {
  const [costs, setCosts] = useState({
    puckCost: 2.50,
    wireCostPerFoot: 0.75,
    connectorCost: 1.50,
    transformerCost: 20.00,
    controllerCost: 20.00,
    laborHourlyRate: 27.50
  });

  const materials = useMemo(() => {
    if (!sections || sections.length === 0) {
      return null;
    }

    // Get parameters with defaults
    const spacingInches = parameters.light_spacing_inches || 12;
    const transformerWatts = parameters.transformer_watts || 36;
    const lightType = parameters.light_type || 'rgbic_puck';
    const voltage = parameters.voltage || '24v';

    // Determine watts per puck based on voltage and type
    let wattsPerPuck = 0.5; // default
    if (voltage === '5v') wattsPerPuck = 0.3;
    else if (voltage === '12v') wattsPerPuck = 0.5;
    else if (voltage === '24v') wattsPerPuck = 0.6;
    
    // Calculate total lighting length
    const totalFeet = sections.reduce((sum, s) => sum + (s.length_feet || 0), 0);
    
    // Calculate number of pucks needed
    const totalInches = totalFeet * 12;
    const puckCount = Math.ceil(totalInches / spacingInches);
    
    // Calculate wire needed: total section length + all jump/connection lengths + 10% waste
    const sectionWire = totalFeet;
    const jumpWire = (jumps || []).reduce((sum, j) => sum + (j.length_feet || 0), 0);
    const wireNeeded = Math.ceil((sectionWire + jumpWire) * 1.1);
    
    // Calculate connectors (one per section + one per jump + 3 extras)
    const connectorCount = sections.length + (jumps?.length || 0) + 3;
    
    // Calculate transformers needed based on selected transformer capacity
    const totalWattage = puckCount * wattsPerPuck;
    const transformersNeeded = Math.ceil(totalWattage / transformerWatts);
    
    // Calculate install time (matches installTimeCalculator.js)
    const setupTime = 1.0; // 1 hour
    const sectionTime = sections.length * 0.5; // 0.5hr per section
    const puckTime = (puckCount / 10) * 0.25; // 15 min per 10 pucks (0.25hr)
    const jumpTime = (jumps?.length || 0) * 0.5; // 0.5hr per jump
    const totalInstallHours = Math.ceil((setupTime + sectionTime + puckTime + jumpTime) * 2) / 2; // Round to nearest 0.5hr
    
    // Calculate costs
    const pucksCost = puckCount * costs.puckCost;
    const wireCost = wireNeeded * costs.wireCostPerFoot;
    const connectorsCost = connectorCount * costs.connectorCost;
    const transformersCost = transformersNeeded * costs.transformerCost;
    const controllersCost = costs.controllerCost;
    const laborCost = totalInstallHours * costs.laborHourlyRate;
    const materialsCost = pucksCost + wireCost + connectorsCost + transformersCost + controllersCost;
    const totalCost = materialsCost + laborCost;
    
    return {
      pucks: { qty: puckCount, unitCost: costs.puckCost, total: pucksCost },
      wire: { qty: wireNeeded, unitCost: costs.wireCostPerFoot, total: wireCost, unit: 'ft' },
      connectors: { qty: connectorCount, unitCost: costs.connectorCost, total: connectorsCost },
      transformers: { qty: transformersNeeded, unitCost: costs.transformerCost, total: transformersCost, watts: transformerWatts },
      controller: { qty: 1, unitCost: costs.controllerCost, total: controllersCost },
      labor: { hours: totalInstallHours, hourlyRate: costs.laborHourlyRate, total: laborCost },
      materialsCost,
      laborCost,
      totalCost,
      totalWattage
    };
  }, [sections, jumps, parameters, costs]);

  // Notify parent of cost changes
  React.useEffect(() => {
    if (materials && onCostCalculated) {
      onCostCalculated(materials.totalCost);
    }
  }, [materials, onCostCalculated]);

  const updateCost = (field, value) => {
    setCosts(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const handlePrint = () => {
    window.print();
  };

  if (!materials) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Add sections to see materials list
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cost Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Cost Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Puck Cost</Label>
              <Input
                type="number"
                step="0.01"
                value={costs.puckCost}
                onChange={(e) => updateCost('puckCost', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Wire Cost/Foot</Label>
              <Input
                type="number"
                step="0.01"
                value={costs.wireCostPerFoot}
                onChange={(e) => updateCost('wireCostPerFoot', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Connector Cost</Label>
              <Input
                type="number"
                step="0.01"
                value={costs.connectorCost}
                onChange={(e) => updateCost('connectorCost', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Transformer Cost</Label>
              <Input
                type="number"
                step="0.01"
                value={costs.transformerCost}
                onChange={(e) => updateCost('transformerCost', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Controller Cost</Label>
              <Input
                type="number"
                step="0.01"
                value={costs.controllerCost}
                onChange={(e) => updateCost('controllerCost', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Labor Rate/Hour</Label>
              <Input
                type="number"
                step="0.01"
                value={costs.laborHourlyRate}
                onChange={(e) => updateCost('laborHourlyRate', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Materials Shopping List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-amber-600" />
            Materials Shopping List
          </CardTitle>
          <Button onClick={handlePrint} variant="outline" size="sm">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Pucks */}
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium">LED Puck Lights</div>
                <div className="text-sm text-slate-500">
                  {materials.pucks.qty} units @ ${materials.pucks.unitCost.toFixed(2)} each
                </div>
              </div>
              <div className="text-lg font-semibold text-slate-900">
                ${materials.pucks.total.toFixed(2)}
              </div>
            </div>

            {/* Wire */}
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium">Wire (18/2 Low Voltage)</div>
                <div className="text-sm text-slate-500">
                  {materials.wire.qty} ft @ ${materials.wire.unitCost.toFixed(2)}/ft
                </div>
              </div>
              <div className="text-lg font-semibold text-slate-900">
                ${materials.wire.total.toFixed(2)}
              </div>
            </div>

            {/* Connectors */}
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium">Wire Connectors</div>
                <div className="text-sm text-slate-500">
                  {materials.connectors.qty} units @ ${materials.connectors.unitCost.toFixed(2)} each
                </div>
              </div>
              <div className="text-lg font-semibold text-slate-900">
                ${materials.connectors.total.toFixed(2)}
              </div>
            </div>

            {/* Transformers */}
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium">Transformers ({materials.transformers.watts}W)</div>
                <div className="text-sm text-slate-500">
                  {materials.transformers.qty} units @ ${materials.transformers.unitCost.toFixed(2)} each
                  <span className="ml-2 text-xs">({materials.totalWattage.toFixed(1)}W total load)</span>
                </div>
              </div>
              <div className="text-lg font-semibold text-slate-900">
                ${materials.transformers.total.toFixed(2)}
              </div>
            </div>

            {/* Controller */}
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium">Smart Controller</div>
                <div className="text-sm text-slate-500">
                  {materials.controller.qty} unit
                </div>
              </div>
              <div className="text-lg font-semibold text-slate-900">
                ${materials.controller.total.toFixed(2)}
              </div>
            </div>

            {/* Materials Subtotal */}
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
              <div className="font-semibold text-blue-900">Materials Subtotal</div>
              <div className="text-xl font-bold text-blue-900">
                ${materials.materialsCost.toFixed(2)}
              </div>
            </div>

            {/* Labor */}
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium">Installation Labor</div>
                <div className="text-sm text-slate-500">
                  {materials.labor.hours} hours @ ${materials.labor.hourlyRate.toFixed(2)}/hr
                </div>
              </div>
              <div className="text-lg font-semibold text-slate-900">
                ${materials.labor.total.toFixed(2)}
              </div>
            </div>

            {/* Total Cost */}
            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border-2 border-green-300">
              <div className="font-bold text-green-900 text-lg">Total Project Cost</div>
              <div className="text-2xl font-bold text-green-900">
                ${materials.totalCost.toFixed(2)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Install Time Estimate */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-600" />
            Install Time Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Setup & Planning</span>
              <span className="font-medium">1.0 hours</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Section Installation ({sections.length} sections)</span>
              <span className="font-medium">{(sections.length * 0.5).toFixed(1)} hours</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Puck Mounting ({materials.pucks.qty} pucks)</span>
              <span className="font-medium">{((materials.pucks.qty / 10) * 0.25).toFixed(1)} hours</span>
            </div>
            {jumps && jumps.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Jump Wire Runs ({jumps.length} jumps)</span>
                <span className="font-medium">{(jumps.length * 0.5).toFixed(1)} hours</span>
              </div>
            )}
            <div className="flex justify-between pt-3 border-t font-semibold">
              <span>Total Install Time</span>
              <span className="text-lg text-amber-600">{materials.labor.hours} hours</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
